import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { ReactComponent as FilterIcon } from '../assets/svg/icon-filter.svg';
import './MachinePriority.css';
import '../boxitem.css';
import '../topmenu.css';
import '../forms.css';
import GroupedDataTable from './GroupDataTable2';
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import { jwtDecode } from 'jwt-decode';

const MachinePriority = () => {
  const { t } = useTranslation();
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const [machinePriorities, setMachinePriorities] = useState([]);
  const [machineGroups, setMachineGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [permissions, setPermissions] = useState({ can_edit: false, can_view: true });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const fetchPermissions = async () => {
        try {
          const response = await axios.get(`${apiBaseUrl}/mnt/api/roles/permissions/${decodedToken.user_rights}`);
          setPermissions({
            can_edit: response.data.some(p => p.permission_name === 'can_edit' && p.can_edit === 1),
            can_view: response.data.some(p => p.permission_name === 'can_view' && p.can_view === 1)
          });
        } catch (error) {
          console.error('Error fetching permissions:', error);
        }
      };
      fetchPermissions();
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    const fetchMachineGroups = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/mnt/api/machines/groups`);
        setMachineGroups([t('machinePriority.all'), ...response.data]);
      } catch (error) {
        console.error('Error fetching machine groups:', error);
      }
    };

    const fetchMachinePriorities = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/mnt/api/machines/priority`, {
          params: { group: selectedGroup },
        });
        setMachinePriorities(response.data);
      } catch (error) {
        console.error('Error fetching machine priorities:', error);
      }
    };

    fetchMachineGroups();
    fetchMachinePriorities();
  }, [selectedGroup, apiBaseUrl, t]);

  const handlePriorityChange = async (item, newPriority) => {
    if (!permissions.can_edit) return;
    try {
      await axios.post(`${apiBaseUrl}/mnt/api/machines/priority`, {
        machineid: item.machineid,
        priority: newPriority
      });
      setMachinePriorities(prev =>
        prev.map(m => m.machineid === item.machineid ? { ...m, priority: newPriority } : m)
      );
    } catch (error) {
      console.error('Error saving machine priority:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'red';
      case 'Medium': return 'yellow';
      case 'Low': return 'green';
      default: return '';
    }
  };

  const addColor = (items, color) =>
    items.map(i => ({ ...i, _groupKey: color, _color: getPriorityColor(i.priority) }));

  const groupedItemsRaw = {
    red: [],
    yellow: [],
    green: []
  };

  machinePriorities.forEach((m) => {
    const color = getPriorityColor(m.priority);
    if (groupedItemsRaw[color]) {
      groupedItemsRaw[color].push({
        ...m,
        item_id: m.machineid,
        item_name: m.machine,
        item_type: m.dncmaxgroupname,
      });
    }
  });

  const groupedData = Object.fromEntries(
    Object.entries(groupedItemsRaw).map(([color, items]) => [
      t(`machinePriority.${color}`),
      addColor(items, color)
    ])
  );

  return (
    <>
      {showFilters && (
        <div className="filter-container" onClick={() => setShowFilters(!showFilters)}>
          <div className="filter" onClick={(e) => e.stopPropagation()}>
            <div className="filter__fields">
              <div className="filter__label">{t('machinePriority.groupLabel')}</div>
              <div className="entry-item form-select">
                <select
                  id="machineGroup"
                  className="form-select"
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                >
                  {machineGroups.map((group, index) => (
                    <option key={index} value={group}>{group}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        <div className="top-menu">
          <div className="top-menu__divider"></div>
          <div className="top-menu__icon-button" onClick={() => setShowFilters(!showFilters)}><FilterIcon /></div>
        </div>

        <div className="content__main-title">{t('machinePriority.title')}</div>

        <GroupedDataTable
          groupedData={groupedData}
          isFiltered={false}
          headers={[
            {
              label: t("machinePriority.machine"),
              accessor: "item_name",
              elasticCell: true,
              colorStyle: "block",
              colorValueFrom: item => item._color || "grey"
            },
            {
              label: t("machinePriority.group"),
              accessor: "item_type",
              hideMobile: true
            },
            {
              label: t("machinePriority.priority"),
              render: (item) => (
                permissions.can_edit ? (
                  <select
                    className="form-select table-list-2__form-select"
                    value={item.priority}
                    onChange={(e) => handlePriorityChange(item, e.target.value)}
                  >
                    <option value="High">{t('machinePriority.high')}</option>
                    <option value="Medium">{t('machinePriority.medium')}</option>
                    <option value="Low">{t('machinePriority.low')}</option>
                  </select>
                ) : item.priority
              ),
              hideMobile: true
            },
          ]}
          rowKeyFn={(item) => item.item_id}
          groupMeta={{
            [t("machinePriority.high")]: {
              title: t("machinePriority.high"),
              description: t("machinePriority.descHigh"),
              emptyMessage: t("machinePriority.noHigh"),
            },
            [t("machinePriority.medium")]: {
              title: t("machinePriority.medium"),
              description: t("machinePriority.descMedium"),
              emptyMessage: t("machinePriority.noMedium"),
            },
            [t("machinePriority.low")]: {
              title: t("machinePriority.low"),
              description: t("machinePriority.descLow"),
              emptyMessage: t("machinePriority.noLow"),
            }
          }}
        />
        {/*contextualOptionsFn={() => []}*/}
      </div>
    </>
  );
};

export default MachinePriority;
